<template>
    <div class="relative min-h-screen w-full flex bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50">
        <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-6 max-w-6xl">
            <!-- Breadcrumb -->
            <nav class="text-sm text-gray-600 mb-6 flex flex-wrap items-center">
                <router-link to="/dashboard"
                    class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
                <span class="mx-1">/</span>
                <router-link to="/survey-and-feedback-center"
                    class="hover:text-blue-700 transition font-medium">Survey & Feedback Center</router-link>
                <span class="mx-1">/</span>
                <router-link to="/survey"
                    class="hover:text-blue-700 transition font-medium">Surveys</router-link>
                <span class="mx-1">/</span>
                <span class="text-blue-900 font-semibold">Analysis</span>
            </nav>

            <!-- Header Card -->
            <div class="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 mb-6">
                <div class="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 px-6 sm:px-8 py-6">
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div class="flex items-center space-x-4 flex-1">
                            <div class="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                                <font-awesome-icon icon="chart-pie" class="text-2xl text-white" />
                            </div>
                            <div class="flex-1">
                                <h1 class="text-2xl sm:text-3xl font-bold text-white">
                                    {{ survey ? survey.title : 'Survey Analysis' }}
                                </h1>
                                <p class="text-blue-100 text-sm sm:text-base">Visual breakdown of how respondents
                                    answered each question</p>
                            </div>
                        </div>
                        <div class="flex items-center space-x-3 sm:space-x-4">
                            <button @click="goBack"
                                class="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg font-medium transition-all shadow-sm hover:shadow-md flex items-center">
                                <font-awesome-icon icon="arrow-left" class="mr-2" />
                                <span class="hidden sm:inline">Back</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="flex flex-col justify-center items-center min-h-96">
                <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
                <p class="mt-4 text-gray-600 text-lg">Loading survey...</p>
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="bg-white rounded-xl shadow-lg border border-red-200 p-8 text-center">
                <font-awesome-icon icon="exclamation-circle" class="text-5xl text-red-500 mb-4" />
                <h2 class="text-2xl font-bold text-gray-900 mb-2">Error Loading Survey</h2>
                <p class="text-gray-600 mb-4">{{ error }}</p>
                <button @click="goBack"
                    class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors">
                    Go Back
                </button>
            </div>

            <!-- Main Content -->
            <div v-else class="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                <!-- Section Header -->
                <div class="px-6 sm:px-8 py-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center">
                            <div class="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                                <font-awesome-icon icon="chart-pie" class="text-white" />
                            </div>
                            <div>
                                <h2 class="text-lg font-semibold text-gray-900">Survey Analysis</h2>
                                <p class="text-sm text-gray-600">Question-wise visual breakdown</p>
                            </div>
                        </div>
                        <div class="flex items-center space-x-3">
                            <button @click="exportToPdf" :disabled="exportLoading"
                                class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium text-sm transition-colors flex items-center disabled:opacity-50">
                                <font-awesome-icon v-if="exportLoading" icon="spinner" spin class="mr-2" />
                                <font-awesome-icon v-else icon="file-pdf" class="mr-2" />
                                Export PDF
                            </button>
                            <span
                                class="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 font-medium text-sm">
                                <font-awesome-icon icon="clipboard-check" class="mr-1" />
                                {{ totalResponses }} {{ totalResponses === 1 ? 'Response' : 'Responses' }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Content (exported to PDF) -->
                <div ref="surveyAnalysisContent" class="p-6 sm:p-8">
                    <!-- Analysis Loading State -->
                    <div v-if="analysisLoading" class="flex flex-col items-center justify-center py-16">
                        <base-page-spinner type="spin" class-list="h-16"></base-page-spinner>
                        <p class="mt-4 text-gray-600 text-base">Loading analysis...</p>
                    </div>

                    <!-- Analysis Content -->
                    <div v-else>
                        <div v-if="!analysisQuestions || analysisQuestions.length === 0"
                            class="bg-gray-50 rounded-xl border border-dashed border-gray-300 p-10 text-center">
                            <div class="text-4xl mb-2">📊</div>
                            <h3 class="text-lg font-semibold text-gray-900 mb-1">No analysis data available</h3>
                            <p class="text-gray-600 text-sm">
                                Once responses are recorded for this survey, you will see question-wise charts here.
                            </p>
                        </div>
                        <div v-else class="grid gap-6 md:grid-cols-2" id="survey-analysis-charts">
                            <div v-for="(question, index) in analysisQuestions" :key="question.question_id"
                                class="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col">
                                <div class="mb-3">
                                    <div class="flex items-start justify-between">
                                        <div class="flex-1">
                                            <p class="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">
                                                Question {{ index + 1 }}
                                            </p>
                                            <p class="text-sm sm:text-base font-semibold text-gray-900">
                                                {{ question.question_text }}
                                            </p>
                                        </div>
                                        <span
                                            class="ml-3 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700">
                                            {{ question.type }}
                                        </span>
                                    </div>
                                    <p class="mt-1 text-xs text-gray-500">
                                        {{ question.total_responses }} {{ question.total_responses === 1 ? 'response' :
                                            'responses' }}
                                    </p>
                                </div>

                                <div v-if="question.has_chart && question.stats && question.stats.length"
                                    class="mt-2 flex-1 flex flex-col chart-export-wrapper">
                                    <apexchart type="pie" height="320" :options="getPieChartOptions(question)"
                                        :series="getPieChartSeries(question)" />
                                </div>
                                <div v-else
                                    class="mt-4 text-gray-500 text-sm bg-gray-50 border border-dashed border-gray-200 rounded-lg p-4">
                                    <p class="flex items-center">
                                        <font-awesome-icon icon="info-circle" class="mr-2 text-gray-400" />
                                        This question is open-ended or does not have structured options, so a chart is
                                        not
                                        available.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { gql } from '@apollo/client/core';
import html2pdf from 'html2pdf.js';

const GET_SURVEY_BY_SLUG = gql`
  query GetSurveyBySlug($slug: String!) {
    surveyBySlug(slug: $slug) {
      error
      error_code
      message
      data {
        id
        title
        display_title
        description
        slug
        is_active
        thumbnail_path
        thumbnail_url
        responses_count
      }
    }
  }
`;

const GET_SURVEY_ANALYSIS = gql`
  query GetSurveyAnalysis($survey_id: ID!) {
    surveyAnalysis(survey_id: $survey_id) {
      error
      error_code
      message
      data {
        question_id
        question_text
        type
        has_chart
        total_responses
        stats {
          option_id
          label
          count
          percentage
        }
      }
    }
  }
`;

export default {
    name: 'SurveyAnalysis',
    data() {
        return {
            survey: null,
            analysisLoading: true,
            analysisQuestions: [],
            loading: true,
            error: null,
            exportLoading: false,
        };
    },
    computed: {
        totalResponses() {
            return this.survey ? (this.survey.responses_count || 0) : 0;
        },
    },
    created() {
        this.loadSurvey();
    },
    methods: {
        async loadSurvey() {
            try {
                const slug = this.$route.params.slug;
                const { data } = await this.$apollo.query({
                    query: GET_SURVEY_BY_SLUG,
                    variables: { slug: slug },
                    fetchPolicy: 'network-only',
                });

                if (!data || !data.surveyBySlug) {
                    this.error = 'Failed to load survey.';
                    this.loading = false;
                    return;
                }

                const result = data.surveyBySlug;

                if (result.error) {
                    this.error = result.message || 'Failed to load survey.';
                    this.loading = false;
                    return;
                }

                this.survey = result.data;

                if (!this.survey.is_active) {
                    this.error = 'This survey is currently inactive.';
                    this.loading = false;
                    return;
                }

                // Load analysis after survey is loaded
                this.loadAnalysis();
            } catch (error) {
                console.error(error);
                let errorMessage = 'Failed to load survey. Please try again later.';
                if (error.graphQLErrors && error.graphQLErrors.length > 0 && error.graphQLErrors[0].message) {
                    errorMessage = error.graphQLErrors[0].message;
                } else if (error.networkError && error.networkError.message) {
                    errorMessage = error.networkError.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }
                this.error = errorMessage;
                this.loading = false;
            }
        },
        async loadAnalysis() {
            if (!this.survey || !this.survey.id) {
                this.analysisLoading = false;
                return;
            }
            this.analysisLoading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: GET_SURVEY_ANALYSIS,
                    variables: { survey_id: this.survey.id },
                    fetchPolicy: 'network-only',
                });

                if (!data || !data.surveyAnalysis) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Failed to load survey analysis.',
                    });
                    this.analysisLoading = false;
                    return;
                }

                const result = data.surveyAnalysis;

                if (result.error) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: result.message || 'Failed to load survey analysis.',
                    });
                    this.analysisLoading = false;
                    return;
                }

                this.analysisQuestions = result.data || [];
            } catch (error) {
                let errorMessage = 'Failed to load survey analysis.';
                if (error.graphQLErrors && error.graphQLErrors.length > 0 && error.graphQLErrors[0].message) {
                    errorMessage = error.graphQLErrors[0].message;
                } else if (error.networkError && error.networkError.message) {
                    errorMessage = error.networkError.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }
                this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: errorMessage,
                });
            } finally {
                this.analysisLoading = false;
                this.loading = false;
            }
        },
        getChartColors() {
            return [
                '#1f77b4',
                '#ff7f0e',
                '#2ca02c',
                '#d62728',
                '#9467bd',
                '#8c564b',
                '#e377c2',
                '#7f7f7f',
                '#bcbd22',
                '#17becf',
            ];
        },
        getPieChartSeries(question) {
            if (!question || !question.stats || !question.stats.length) {
                return [];
            }
            return question.stats.map(function (item) {
                return item.count;
            });
        },
        getPieChartOptions(question) {
            const labels = (question && question.stats)
                ? question.stats.map(function (item) {
                    return item.label;
                })
                : [];
            const colors = this.getChartColors();

            return {
                labels: labels,
                colors: colors,
                legend: {
                    position: 'bottom',
                    formatter: function (seriesName, opts) {
                        const seriesIndex = opts.seriesIndex;
                        const counts = opts.w.config.series;
                        const value = counts && counts.length > seriesIndex ? counts[seriesIndex] : 0;
                        return seriesName + ' - ' + value + ' responses';
                    },
                },
                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        return val.toFixed(1) + '%';
                    },
                },
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return val + ' responses';
                        },
                    },
                },
            };
        },
        exportToPdf() {
            const element = this.$refs.surveyAnalysisContent;
            if (!element) {
                this.$swal.fire({
                    icon: 'error',
                    title: 'Export Failed',
                    text: 'Content not found.',
                });
                return;
            }
            this.exportLoading = true;
            const fileName = 'survey_analysis_' + (this.survey && this.survey.title ? this.survey.title.replace(/[^a-z0-9]/gi, '_') : 'export') + '.pdf';
            const opt = {
                margin: 10,
                filename: fileName,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2 },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
            };
            html2pdf().set(opt).from(element).save().then(() => {
                this.exportLoading = false;
                this.$swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'PDF downloaded',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });
            }).catch((err) => {
                this.exportLoading = false;
                console.error('Export error:', err);
                this.$swal.fire({
                    icon: 'error',
                    title: 'Export Failed',
                    text: err && err.message ? err.message : 'Failed to export PDF.',
                });
            });
        },
        goBack() {
            this.$router.go(-1);
        },
    },
};
</script>

<style lang="scss" scoped>
// Add any specific styles if needed</style>
